import React, { useState, useEffect } from 'react';
import { 
  FaHome, FaClock, FaLaptop, FaCertificate, FaMoneyBillWave, FaMobileAlt,
  FaPlay, FaUsers, FaCheckCircle, FaStar, FaGraduationCap,
  FaBook, FaVideo, FaHeadset, FaRocket, FaAward,
  FaChartLine,  FaClipboardList
} from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ELearningPage = () => {
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [counts, setCounts] = useState({ students: 0, hours: 0, satisfaction: 0 });

  // Intersection Observer pour animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observerCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setVisibleSections(prev => new Set([...prev, entry.target.dataset.section]));
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    document.querySelectorAll('[data-section]').forEach(section => {
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  // Animation des compteurs
  useEffect(() => {
    const targets = { students: 5000, hours: 50000, satisfaction: 98 };
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;
    const increment = {
      students: targets.students / steps,
      hours: targets.hours / steps,
      satisfaction: targets.satisfaction / steps
    };

    let currentStep = 0;
    const timer = setInterval(() => {
      if (currentStep < steps) {
        setCounts({
          students: Math.min(Math.floor(increment.students * currentStep), targets.students),
          hours: Math.min(Math.floor(increment.hours * currentStep), targets.hours),
          satisfaction: Math.min(Math.floor(increment.satisfaction * currentStep), targets.satisfaction)
        });
        currentStep++;
      } else {
        clearInterval(timer);
        setCounts(targets);
      }
    }, interval);

    return () => clearInterval(timer);
  }, []);

  const advantages = [
    {
      icon: FaHome,
      title: 'Apprenez de Chez Vous',
      description: 'Suivez vos formations professionnelles depuis le confort de votre domicile ou bureau, sans aucune contrainte de déplacement.',
      color: '#fd7e14',
      stats: '100% en ligne'
    },
    {
      icon: FaClock,
      title: 'Horaires 100% Flexibles',
      description: 'Organisez votre temps d\'apprentissage selon VOS disponibilités. Accédez à vos cours 24h/24 et 7j/7, à votre rythme.',
      color: '#198754',
      stats: '24/7 disponible'
    },
    {
      icon: FaLaptop,
      title: 'Plateforme Interactive Moderne',
      description: 'Interface intuitive avec vidéos HD, quiz interactifs, forums de discussion et suivi de progression en temps réel.',
      color: '#8b5cf6',
      stats: 'Multi-devices'
    },
    {
      icon: FaCertificate,
      title: 'Certification Officielle CINEF',
      description: 'Obtenez exactement les mêmes certifications que les formations en présentiel, reconnues internationalement.',
      color: '#dc3545',
      stats: 'Reconnue'
    },
    {
      icon: FaMoneyBillWave,
      title: 'Économies Substantielles',
      description: 'Réduisez considérablement vos coûts : aucun frais de déplacement, hébergement, restauration ou temps de trajet.',
      color: '#20c997',
      stats: 'Jusqu\'à -40%'
    },
    {
      icon: FaMobileAlt,
      title: 'Accessible Partout',
      description: 'Formez-vous depuis n\'importe quel appareil : ordinateur, tablette ou smartphone. Votre formation vous suit partout.',
      color: '#fbbf24',
      stats: 'iOS & Android'
    }
  ];

  const features = [
    { icon: FaVideo, title: 'Vidéos HD', description: 'Cours vidéo en haute définition' },
    { icon: FaBook, title: 'Documents PDF', description: 'Support de cours téléchargeables' },
    { icon: FaUsers, title: 'Webinaires Live', description: 'Sessions en direct avec formateurs' },
    { icon: FaHeadset, title: 'Support 24/7', description: 'Assistance technique permanente' },
    { icon: FaChartLine, title: 'Suivi Progression', description: 'Dashboard personnalisé' },
    { icon: FaClipboardList, title: 'Quiz Interactifs', description: 'Évaluations régulières' }
  ];

  const testimonials = [
    {
      name: 'Aminata Traoré',
      role: 'Comptable',
      company: 'Abidjan, Côte d\'Ivoire',
      avatar: 'https://i.pravatar.cc/150?img=1',
      text: 'Grâce au e-learning CINEF, j\'ai pu suivre ma formation tout en continuant à travailler. La flexibilité des horaires a été un vrai game-changer pour moi !',
      rating: 5
    },
    {
      name: 'Kofi Mensah',
      role: 'Entrepreneur',
      company: 'Lomé, Togo',
      avatar: 'https://i.pravatar.cc/150?img=3',
      text: 'La plateforme est vraiment bien faite. Les vidéos sont claires, les supports sont complets et j\'ai obtenu ma certification sans problème.',
      rating: 5
    },
    {
      name: 'Mariama Diallo',
      role: 'Manager RH',
      company: 'Cotonou, Bénin',
      avatar: 'https://i.pravatar.cc/150?img=5',
      text: 'Une formation de qualité à un prix très compétitif. Le fait de ne pas avoir à me déplacer m\'a fait économiser temps et argent. Je recommande vivement !',
      rating: 5
    }
  ];

  return (
    <>
    <Header />
    <div className="elearning-wrapper">
      {/* Hero Section */}
      <section className="elearning-hero">
        <div className="hero-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
        </div>

        <div className="elearning-container">
          <div className="hero-content-grid">
            <div className="hero-left-content">
              <div className="hero-badge-elearning">
                <FaRocket className="badge-icon-animated" />
                <span>Formations 100% à Distance</span>
              </div>

              <h1 className="hero-title-elearning">
                Apprenez de n'importe où, <br />
                <span className="gradient-text-elearning">à votre rythme</span>
              </h1>

              <p className="hero-description-elearning">
                Développez vos compétences professionnelles avec nos formations e-learning certifiantes. 
                Accédez à plus de 200 formations en ligne, disponibles 24/7, depuis n'importe quel appareil.
              </p>

              <div className="hero-features-list">
                <div className="feature-check">
                  <FaCheckCircle />
                  <span>Accès immédiat après inscription</span>
                </div>
                <div className="feature-check">
                  <FaCheckCircle />
                  <span>Certification reconnue internationalement</span>
                </div>
                <div className="feature-check">
                  <FaCheckCircle />
                  <span>Support pédagogique inclus</span>
                </div>
              </div>

              <div className="hero-cta-buttons">
                <a href="/contact" className="btn-primary-elearning">
                  <FaGraduationCap />
                  Demander un suivi de formation
                </a>
                <button className="btn-secondary-elearning">
                  <FaPlay />
                  Voir la Démo
                </button>
              </div>

              <div className="hero-stats-inline">
                <div className="stat-inline">
                  <div className="stat-number-inline">{counts.students}+</div>
                  <div className="stat-label-inline">Apprenants</div>
                </div>
                <div className="stat-inline">
                  <div className="stat-number-inline">{counts.hours}+</div>
                  <div className="stat-label-inline">Heures de Contenu</div>
                </div>
                <div className="stat-inline">
                  <div className="stat-number-inline">{counts.satisfaction}%</div>
                  <div className="stat-label-inline">Satisfaction</div>
                </div>
              </div>
            </div>

            <div className="hero-right-content">
              <div className="hero-image-container-elearning">
                <img 
                  src="https://images.unsplash.com/photo-1588196749597-9ff075ee6b5b?w=800&q=80" 
                  alt="E-learning CINEF" 
                  className="hero-main-img"
                />
                
                <div className="floating-badge badge-2">
                  <FaAward className="float-icon-badge" />
                  <div className="float-text-badge">
                    <div className="float-number-badge">98%</div>
                    <div className="float-label-badge">Réussite</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Advantages Section - MISE EN AVANT */}
      <section 
        className={`advantages-section-major ${visibleSections.has('advantages') ? 'visible' : ''}`}
        data-section="advantages"
      >
        <div className="elearning-container">
          <div className="section-header-major">
            <div className="section-badge-elearning">
              <span className="badge-dot-elearning"></span>
              <span>Pourquoi choisir le E-Learning CINEF ?</span>
            </div>
            <h2 className="section-title-major">
              Les avantages de la formation <br />
              <span className="highlight-text-elearning">100% à distance</span>
            </h2>
            <p className="section-subtitle-major">
              Découvrez tous les bénéfices de nos formations en ligne et transformez 
              votre manière d'apprendre avec une flexibilité totale.
            </p>
          </div>

          <div className="advantages-grid-major">
            {advantages.map((advantage, index) => {
              const Icon = advantage.icon;
              return (
                <div 
                  key={index} 
                  className="advantage-card-major"
                  style={{ '--advantage-color': advantage.color }}
                >
                  <div className="advantage-header-card">
                    <div className="advantage-icon-major">
                      <Icon />
                    </div>
                    <div className="advantage-stats-badge">{advantage.stats}</div>
                  </div>
                  <h3 className="advantage-title-major">{advantage.title}</h3>
                  <p className="advantage-description-major">{advantage.description}</p>
                  <div className="advantage-checkmark-major">
                    <FaCheckCircle />
                  </div>
                </div>
              );
            })}
          </div>

          {/* CTA après les avantages */}
          <div className="advantages-cta-box">
            <div className="cta-content-advantages">
              <h3>Prêt à Commencer votre formation en ligne ?</h3>
              <p>Rejoignez plus de 5000 apprenants qui ont déjà choisi CINEF E-Learning</p>
            </div>
            <a href="/recueil" className="btn-cta-advantages">
             Inscrivez vous dès maintenant
             
            </a>
          </div>
        </div>
      </section>

      {/* Platform Features */}
      <section 
        className={`platform-section ${visibleSections.has('platform') ? 'visible' : ''}`}
        data-section="platform"
      >
        <div className="elearning-container">
          <div className="platform-grid">
            <div className="platform-left">
              <div className="section-badge-elearning">
                <span className="badge-dot-elearning"></span>
                <span>Notre plateforme</span>
              </div>
              <h2 className="section-title-platform">
                Une plateforme <br />
                <span className="highlight-text-elearning">moderne & intuitive</span>
              </h2>
              <p className="section-description-platform">
                Profitez d'une expérience d'apprentissage optimale avec notre plateforme 
                e-learning dernière génération, conçue pour votre réussite.
              </p>

              <div className="features-list-platform">
                {features.map((feature, index) => {
                  const Icon = feature.icon;
                  return (
                    <div key={index} className="feature-item-platform">
                      <div className="feature-icon-platform">
                        <Icon />
                      </div>
                      <div className="feature-content-platform">
                        <h4>{feature.title}</h4>
                        <p>{feature.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <a href="https://cinef-formations.trainercentralsite.com/#/allcourses" className="btn-platform" target="_blank" rel="noopener noreferrer">
                Accéder à la Plateforme
               
              </a>
            </div>

            <div className="platform-right">
              <div className="platform-mockup">
                <img 
                  src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&q=80" 
                  alt="Plateforme CINEF E-Learning" 
                  className="mockup-image"
                />
                <div className="mockup-overlay">
                  <div className="play-button-mockup">
                    <FaPlay />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section 
        className={`testimonials-elearning ${visibleSections.has('testimonials') ? 'visible' : ''}`}
        data-section="testimonials"
      >
        <div className="elearning-container">
          <div className="section-header-center">
            <div className="section-badge-elearning centered">
              <span className="badge-dot-elearning"></span>
              <span>Témoignages</span>
            </div>
            <h2 className="section-title-testimonials">
              Ce Que Disent Nos <br />
              <span className="highlight-text-elearning">Apprenants</span>
            </h2>
          </div>

          <div className="testimonials-grid-elearning">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card-elearning">
                <div className="testimonial-rating-stars">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <FaStar key={i} />
                  ))}
                </div>
                <p className="testimonial-text-elearning">"{testimonial.text}"</p>
                <div className="testimonial-author-elearning">
                  <img src={testimonial.avatar} alt={testimonial.name} />
                  <div className="author-info-elearning">
                    <div className="author-name-elearning">{testimonial.name}</div>
                    <div className="author-role-elearning">{testimonial.role}</div>
                    <div className="author-company-elearning">{testimonial.company}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      
      

      {/* STYLES CSS */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .elearning-wrapper {
          font-family: 'Inter', sans-serif;
          background: #fafafa;
          overflow-x: hidden;
        }

        .elearning-container {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 24px;
        }

        /* Animations au Scroll */
        section {
          opacity: 0;
          transform: translateY(50px);
          transition: opacity 0.8s ease-out, transform 0.8s ease-out;
        }

        section.visible, .elearning-hero {
          opacity: 1;
          transform: translateY(0);
        }

        /* Hero Section */
        .elearning-hero {
          min-height: 90vh;
          display: flex;
          align-items: center;
          background: linear-gradient(135deg, #f8fafc 0%, #ffffff 50%, #eff6ff 100%);
          padding: 120px 0 80px;
          position: relative;
          overflow: hidden;
        }

        .hero-shapes {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 0;
        }

        .shape {
          position: absolute;
          border-radius: 50%;
          opacity: 0.6;
          animation: floatShape 20s ease-in-out infinite;
        }

        .shape-1 {
          width: 600px;
          height: 600px;
          background: radial-gradient(circle, rgba(253, 126, 20, 0.08) 0%, transparent 70%);
          top: -10%;
          right: -10%;
        }

        .shape-2 {
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(25, 135, 84, 0.08) 0%, transparent 70%);
          bottom: -5%;
          left: -5%;
          animation-delay: -5s;
        }

        .shape-3 {
          width: 300px;
          height: 300px;
          background: radial-gradient(circle, rgba(139, 92, 246, 0.08) 0%, transparent 70%);
          top: 50%;
          left: 50%;
          animation-delay: -10s;
        }

        @keyframes floatShape {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -30px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }

        .hero-content-grid {
          display: grid;
          grid-template-columns: 1.1fr 0.9fr;
          gap: 80px;
          align-items: center;
          position: relative;
          z-index: 1;
        }

        .hero-badge-elearning {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: white;
          padding: 12px 24px;
          border-radius: 50px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          margin-bottom: 28px;
          animation: fadeInDown 0.8s ease-out;
        }

        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .badge-icon-animated {
          color: #fd7e14;
          font-size: 20px;
          animation: bounce 2s ease-in-out infinite;
        }

        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }

        .hero-badge-elearning span {
          font-size: 15px;
          font-weight: 700;
          color: #1f2937;
        }

        .hero-title-elearning {
          font-size: 64px;
          font-weight: 900;
          line-height: 1.1;
          color: #111827;
          margin-bottom: 24px;
          animation: fadeInUp 0.8s ease-out 0.2s backwards;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .gradient-text-elearning {
          background: linear-gradient(135deg, #fd7e14 0%, #dc3545 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .hero-description-elearning {
          font-size: 19px;
          line-height: 1.7;
          color: #6b7280;
          margin-bottom: 32px;
          animation: fadeInUp 0.8s ease-out 0.4s backwards;
        }

        .hero-features-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-bottom: 40px;
          animation: fadeInUp 0.8s ease-out 0.6s backwards;
        }

        .feature-check {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 16px;
          color: #374151;
          font-weight: 600;
        }

        .feature-check svg {
          color: #198754;
          font-size: 20px;
          flex-shrink: 0;
        }

        .hero-cta-buttons {
          display: flex;
          gap: 16px;
          margin-bottom: 48px;
          animation: fadeInUp 0.8s ease-out 0.8s backwards;
        }

        .btn-primary-elearning, .btn-secondary-elearning {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 18px 36px;
          border-radius: 14px;
          font-size: 17px;
          font-weight: 700;
          cursor: pointer;
          border: none;
          text-decoration: none;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          font-family: inherit;
        }

        .btn-primary-elearning {
          background: linear-gradient(135deg, #fd7e14 0%, #dc3545 100%);
          color: white;
          box-shadow: 0 12px 40px rgba(253, 126, 20, 0.35);
        }

        .btn-primary-elearning:hover {
          transform: translateY(-5px);
          box-shadow: 0 16px 50px rgba(253, 126, 20, 0.45);
        }

        .btn-secondary-elearning {
          background: white;
          color: #fd7e14;
          border: 2px solid #fd7e14;
        }

        .btn-secondary-elearning:hover {
          background: #fd7e14;
          color: white;
          transform: translateY(-5px);
        }

        .hero-stats-inline {
          display: flex;
          gap: 48px;
          padding-top: 32px;
          border-top: 2px solid #e5e7eb;
          animation: fadeInUp 0.8s ease-out 1s backwards;
        }

        .stat-inline {
          text-align: center;
        }

        .stat-number-inline {
          font-size: 40px;
          font-weight: 900;
          background: linear-gradient(135deg, #fd7e14 0%, #dc3545 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          line-height: 1;
          margin-bottom: 8px;
        }

        .stat-label-inline {
          font-size: 14px;
          color: #6b7280;
          font-weight: 600;
        }

        .hero-right-content {
          animation: fadeInRight 0.8s ease-out 0.3s backwards;
        }

        @keyframes fadeInRight {
          from {
            opacity: 0;
            transform: translateX(50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .hero-image-container-elearning {
          position: relative;
        }

        .hero-main-img {
          width: 100%;
          border-radius: 24px;
          box-shadow: 0 24px 70px rgba(0, 0, 0, 0.18);
        }

        .floating-badge {
          position: absolute;
          background: white;
          padding: 20px 24px;
          border-radius: 16px;
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
          display: flex;
          align-items: center;
          gap: 16px;
          animation: floatBadge 3s ease-in-out infinite;
        }

        @keyframes floatBadge {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-15px); }
        }

        .badge-1 {
          top: 10%;
          left: -10%;
          background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
          color: white;
        }

        .badge-2 {
          bottom: 15%;
          right: -10%;
        }

        .float-icon-badge {
          font-size: 32px;
        }

        .badge-2 .float-icon-badge {
          color: #fd7e14;
        }

        .float-number-badge {
          font-size: 28px;
          font-weight: 900;
          line-height: 1;
        }

        .float-label-badge {
          font-size: 13px;
          opacity: 0.9;
        }

        /* Advantages Section - MAJOR */
        .advantages-section-major {
          padding: 120px 0;
          background: white;
        }

        .section-header-major {
          text-align: center;
          margin-bottom: 70px;
        }

        .section-badge-elearning {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: linear-gradient(135deg, #fff5f0 0%, #ffe8e0 100%);
          padding: 10px 20px;
          border-radius: 50px;
          margin-bottom: 20px;
          border: 1px solid #fed7cc;
        }

        .section-badge-elearning.centered {
          margin: 0 auto 20px;
        }

        .badge-dot-elearning {
          width: 8px;
          height: 8px;
          background: #fd7e14;
          border-radius: 50%;
          animation: pulse 2s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.2); }
        }

        .section-badge-elearning span:not(.badge-dot-elearning) {
          font-size: 14px;
          font-weight: 700;
          color: #fd7e14;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .section-title-major {
          font-size: 52px;
          font-weight: 900;
          color: #111827;
          line-height: 1.15;
          margin-bottom: 20px;
        }

        .highlight-text-elearning {
          background: linear-gradient(135deg, #fd7e14 0%, #dc3545 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .section-subtitle-major {
          font-size: 19px;
          color: #6b7280;
          line-height: 1.7;
          max-width: 700px;
          margin: 0 auto;
        }

        .advantages-grid-major {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 32px;
          margin-bottom: 60px;
        }

        .advantage-card-major {
          background: white;
          padding: 40px 32px;
          border-radius: 20px;
          border: 2px solid #f3f4f6;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
        }

        .advantage-card-major::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 5px;
          background: var(--advantage-color);
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.4s ease;
        }

        .advantage-card-major:hover {
          transform: translateY(-10px);
          border-color: var(--advantage-color);
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
        }

        .advantage-card-major:hover::before {
          transform: scaleX(1);
        }

        .advantage-header-card {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 24px;
        }

        .advantage-icon-major {
          width: 70px;
          height: 70px;
          background: linear-gradient(135deg, rgba(253, 126, 20, 0.1) 0%, rgba(220, 53, 69, 0.1) 100%);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 32px;
          color: var(--advantage-color);
          transition: all 0.3s ease;
        }

        .advantage-card-major:hover .advantage-icon-major {
          transform: scale(1.1) rotate(5deg);
          background: var(--advantage-color);
          color: white;
        }

        .advantage-stats-badge {
          background: var(--advantage-color);
          color: white;
          padding: 6px 14px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 700;
          white-space: nowrap;
        }

        .advantage-title-major {
          font-size: 22px;
          font-weight: 800;
          color: #111827;
          margin-bottom: 16px;
          line-height: 1.3;
        }

        .advantage-description-major {
          font-size: 15px;
          color: #6b7280;
          line-height: 1.7;
          margin-bottom: 20px;
        }

        .advantage-checkmark-major {
          display: flex;
          justify-content: flex-end;
        }

        .advantage-checkmark-major svg {
          color: #198754;
          font-size: 24px;
          opacity: 0;
          transform: scale(0);
          transition: all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
        }

        .advantage-card-major:hover .advantage-checkmark-major svg {
          opacity: 1;
          transform: scale(1);
        }

        /* CTA Box after Advantages */
        .advantages-cta-box {
          background: linear-gradient(135deg, #111827 0%, #1f2937 100%);
          padding: 50px 60px;
          border-radius: 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
          position: relative;
          overflow: hidden;
        }

        .advantages-cta-box::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -10%;
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(253, 126, 20, 0.15) 0%, transparent 70%);
          border-radius: 50%;
        }

        .cta-content-advantages {
          position: relative;
          z-index: 1;
        }

        .cta-content-advantages h3 {
          font-size: 32px;
          font-weight: 900;
          color: white;
          margin-bottom: 12px;
        }

        .cta-content-advantages p {
          font-size: 17px;
          color: #d1d5db;
        }

        .btn-cta-advantages {
          display: flex;
          align-items: center;
          gap: 12px;
          background: linear-gradient(135deg, #fd7e14 0%, #dc3545 100%);
          color: white;
          padding: 18px 36px;
          border-radius: 14px;
          font-size: 17px;
          font-weight: 700;
          text-decoration: none;
          transition: all 0.3s ease;
          position: relative;
          z-index: 1;
          box-shadow: 0 10px 30px rgba(253, 126, 20, 0.3);
        }

        .btn-cta-advantages:hover {
          transform: translateY(-3px);
          box-shadow: 0 15px 40px rgba(253, 126, 20, 0.4);
        }

        /* Platform Section */
        .platform-section {
          padding: 120px 0;
          background: linear-gradient(180deg, #ffffff 0%, #f9fafb 100%);
        }

        .platform-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 80px;
          align-items: center;
        }

        .section-title-platform {
          font-size: 48px;
          font-weight: 900;
          color: #111827;
          line-height: 1.15;
          margin-bottom: 20px;
        }

        .section-description-platform {
          font-size: 17px;
          color: #6b7280;
          line-height: 1.7;
          margin-bottom: 40px;
        }

        .features-list-platform {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 24px;
          margin-bottom: 40px;
        }

        .feature-item-platform {
          display: flex;
          gap: 16px;
          padding: 20px;
          background: white;
          border-radius: 14px;
          border: 1px solid #e5e7eb;
          transition: all 0.3s ease;
        }

        .feature-item-platform:hover {
          border-color: #fd7e14;
          box-shadow: 0 8px 25px rgba(253, 126, 20, 0.1);
          transform: translateY(-3px);
        }

        .feature-icon-platform {
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, #fd7e14 0%, #dc3545 100%);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
          color: white;
          flex-shrink: 0;
        }

        .feature-content-platform h4 {
          font-size: 16px;
          font-weight: 700;
          color: #111827;
          margin-bottom: 6px;
        }

        .feature-content-platform p {
          font-size: 14px;
          color: #6b7280;
          line-height: 1.5;
        }

        .btn-platform {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          background: linear-gradient(135deg, #fd7e14 0%, #dc3545 100%);
          color: white;
          padding: 18px 36px;
          border-radius: 14px;
          font-size: 17px;
          font-weight: 700;
          text-decoration: none;
          transition: all 0.3s ease;
          box-shadow: 0 10px 30px rgba(253, 126, 20, 0.25);
        }

        .btn-platform:hover {
          transform: translateY(-3px);
          box-shadow: 0 15px 40px rgba(253, 126, 20, 0.35);
        }

        .platform-mockup {
          position: relative;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 25px 70px rgba(0, 0, 0, 0.15);
        }

        .mockup-image {
          width: 100%;
          display: block;
        }

        .mockup-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(253, 126, 20, 0.85) 0%, rgba(220, 53, 69, 0.85) 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.4s ease;
        }

        .platform-mockup:hover .mockup-overlay {
          opacity: 1;
        }

        .play-button-mockup {
          width: 80px;
          height: 80px;
          background: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
          color: #fd7e14;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .play-button-mockup:hover {
          transform: scale(1.1);
        }

        /* Testimonials Section */
        .testimonials-elearning {
          padding: 120px 0;
          background: white;
        }

        .section-header-center {
          text-align: center;
          margin-bottom: 70px;
        }

        .section-title-testimonials {
          font-size: 52px;
          font-weight: 900;
          color: #111827;
          line-height: 1.15;
        }

        .testimonials-grid-elearning {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 32px;
        }

        .testimonial-card-elearning {
          background: #f9fafb;
          padding: 40px 32px;
          border-radius: 20px;
          border: 2px solid #e5e7eb;
          transition: all 0.4s ease;
        }

        .testimonial-card-elearning:hover {
          border-color: #fd7e14;
          background: white;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
          transform: translateY(-10px);
        }

        .testimonial-rating-stars {
          display: flex;
          gap: 4px;
          margin-bottom: 20px;
        }

        .testimonial-rating-stars svg {
          color: #fbbf24;
          font-size: 18px;
        }

        .testimonial-text-elearning {
          font-size: 16px;
          color: #374151;
          line-height: 1.7;
          margin-bottom: 28px;
          font-style: italic;
        }

        .testimonial-author-elearning {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .testimonial-author-elearning img {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          border: 3px solid #fd7e14;
        }

        .author-name-elearning {
          font-size: 16px;
          font-weight: 700;
          color: #111827;
          margin-bottom: 4px;
        }

        .author-role-elearning {
          font-size: 14px;
          color: #6b7280;
          margin-bottom: 2px;
        }

        .author-company-elearning {
          font-size: 13px;
          color: #9ca3af;
        }

        /* Final CTA Section */
        .final-cta-elearning {
          padding: 100px 0;
          background: linear-gradient(135deg, #111827 0%, #1f2937 100%);
          position: relative;
          overflow: hidden;
        }

        .final-cta-elearning::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -20%;
          width: 600px;
          height: 600px;
          background: radial-gradient(circle, rgba(253, 126, 20, 0.1) 0%, transparent 70%);
          border-radius: 50%;
        }

        .final-cta-elearning::after {
          content: '';
          position: absolute;
          bottom: -50%;
          right: -20%;
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(220, 53, 69, 0.1) 0%, transparent 70%);
          border-radius: 50%;
        }

        .final-cta-content {
          text-align: center;
          position: relative;
          z-index: 1;
        }

        .cta-icon-large-elearning {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 100px;
          height: 100px;
          background: linear-gradient(135deg, #fd7e14 0%, #dc3545 100%);
          border-radius: 50%;
          font-size: 48px;
          color: white;
          margin-bottom: 32px;
          box-shadow: 0 15px 50px rgba(253, 126, 20, 0.3);
          animation: pulse-icon 2s ease-in-out infinite;
        }

        @keyframes pulse-icon {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        .cta-title-final {
          font-size: 48px;
          font-weight: 900;
          color: white;
          margin-bottom: 20px;
        }

        .cta-subtitle-final {
          font-size: 19px;
          color: #d1d5db;
          margin-bottom: 40px;
          max-width: 700px;
          margin-left: auto;
          margin-right: auto;
        }

        .cta-buttons-final {
          display: flex;
          gap: 16px;
          justify-content: center;
          margin-bottom: 50px;
        }

        .btn-final-primary, .btn-final-secondary {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 18px 36px;
          border-radius: 14px;
          font-size: 17px;
          font-weight: 700;
          text-decoration: none;
          transition: all 0.3s ease;
        }

        .btn-final-primary {
          background: linear-gradient(135deg, #fd7e14 0%, #dc3545 100%);
          color: white;
          box-shadow: 0 12px 40px rgba(253, 126, 20, 0.35);
        }

        .btn-final-primary:hover {
          transform: translateY(-5px);
          box-shadow: 0 16px 50px rgba(253, 126, 20, 0.45);
        }

        .btn-final-secondary {
          background: transparent;
          color: white;
          border: 2px solid white;
        }

        .btn-final-secondary:hover {
          background: white;
          color: #111827;
          transform: translateY(-5px);
        }

        .cta-trust-badges {
          display: flex;
          gap: 48px;
          justify-content: center;
          align-items: center;
        }

        .trust-badge {
          display: flex;
          align-items: center;
          gap: 12px;
          color: #d1d5db;
          font-size: 15px;
          font-weight: 600;
        }

        .trust-badge svg {
          color: #fd7e14;
          font-size: 24px;
        }

        /* Responsive Design */
        @media (max-width: 1024px) {
          .hero-content-grid {
            grid-template-columns: 1fr;
            gap: 50px;
          }

          .hero-title-elearning {
            font-size: 48px;
          }

          .advantages-grid-major {
            grid-template-columns: repeat(2, 1fr);
          }

          .platform-grid {
            grid-template-columns: 1fr;
            gap: 50px;
          }

          .testimonials-grid-elearning {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .elearning-hero {
            padding: 80px 0 60px;
          }

          .hero-title-elearning {
            font-size: 36px;
          }

          .hero-description-elearning {
            font-size: 16px;
          }

          .hero-cta-buttons {
            flex-direction: column;
          }

          .btn-primary-elearning, .btn-secondary-elearning {
            width: 100%;
            justify-content: center;
          }

          .hero-stats-inline {
            gap: 24px;
          }

          .advantages-grid-major {
            grid-template-columns: 1fr;
          }

          .advantages-cta-box {
            flex-direction: column;
            gap: 30px;
            padding: 40px 30px;
            text-align: center;
          }

          .features-list-platform {
            grid-template-columns: 1fr;
          }

          .section-title-major, .section-title-testimonials {
            font-size: 36px;
          }

          .cta-buttons-final {
            flex-direction: column;
          }

          .btn-final-primary, .btn-final-secondary {
            width: 100%;
            justify-content: center;
          }

          .cta-trust-badges {
            flex-direction: column;
            gap: 20px;
          }

          .floating-badge {
            display: none;
          }
        }
      `}</style>
    </div>
    <Footer />
    </>
  );
};

export default ELearningPage;